import React, { useContext, useRef, useState, useEffect } from "react";
import "../AddEventModal/AddEventModal.css";
import { IoCloseOutline, IoDocumentTextOutline } from "react-icons/io5";
import { TbBrandInstagram, TbStack2, TbWorld } from "react-icons/tb";
import { GrAttachment } from "react-icons/gr";
import { CgCloseO } from "react-icons/cg";
import { useLanguage } from "../../../../LanguageProvider";
import { useToaster } from "../../../../ToasterProvider";
import AddModalInputField from "../../../../components/AddModalInputField/AddModalInputField";
import DatePicker from "../../../../components/DatePicker/DatePicker";
import TimePicker from "../../../../components/TimePicker/TimePicker";
import Dropdown from "../../../../components/Dropdown/Dropdown";
import Services from "../../../../services/Services";
import { GeneralContext } from "../../../../GeneralProvider";
import { RiTwitterXFill } from "react-icons/ri";

export default function EditEventModal({
    isOpen,
    onClose,
    getEvents,
    event
}) {

    const getCoverFile = () => {
        if (!event?.cover) return [];
        const parts = event.cover.split("/");
        const filename = parts[parts.length - 1];
        const ext = filename.split(".").pop();
        return [{ url: event.cover, extension: ext, title: filename }];
    };

    const token = localStorage.getItem("token")
    const { setCreateProjectLoading } = useContext(GeneralContext);
    const { getTranslation } = useLanguage();
    const { toastWarning, toastSuccess } = useToaster();
    const [selectedFiles, setSelectedFiles] = useState(getCoverFile());
    const [eventTitle, setEventTitle] = useState(event?.title);
    const [eventDescription, setEventDescription] = useState(event?.description);
    const [startDate, setStartDate] = useState("");
    const [startTime, setStartTime] = useState("");
    const [endDate, setEndDate] = useState("");
    const [endTime, setEndTime] = useState("");

    const [isOnline, setIsOnline] = useState(event?.isOnline === "1" ? true : false);
    const [locationAddress, setLocationAddress] = useState(event?.locationAddress);
    const [organizedBy, setOrganizedBy] = useState(event?.organizedBy);
    const [hasTickets, setHasTickets] = useState(event?.hasTickets === "1" ? true : false);
    const [ticketSaleStartDateTime, setTicketSaleStartDateTime] = useState(() => {
        if (!event?.ticketSaleStartDateTime) return null;
        const ts = new Date(Number(event.ticketSaleStartDateTime) * 1000);
        const yyyy = ts.getFullYear();
        const mm = String(ts.getMonth() + 1).padStart(2, "0");
        const dd = String(ts.getDate()).padStart(2, "0");
        const hh = String(ts.getHours()).padStart(2, "0");
        const mi = String(ts.getMinutes()).padStart(2, "0");
        return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
    });

    const [ticketSaleNotificationEmail, setTicketSaleNotificationEmail] = useState(event?.ticketSaleNotificationEmail);
    const [contactEmail, setContactEmail] = useState(event?.contactEmail);
    const [customerNotificationMailTemplate, setCustomerNotificationMailTemplate] = useState(event?.customerNotificationMailTemplate);
    const [contactPhone, setContactPhone] = useState(event?.contactPhone);
    const [isOnlineWebsite, setIsOnlineWebsite] = useState(event?.website);
    const [website, setWebsite] = useState(event?.socialMediaLinks.website);
    const [instagram, setInstagram] = useState(event?.socialMediaLinks.instagram);
    const [x, setX] = useState(event?.socialMediaLinks.x);
    const [usersCanSendMessage, setUsersCanSendMessage] = useState(false);
    const [isPublic, setIsPublic] = useState(event?.isPublic);
    const fileInputRef = useRef();

    const [scriptLoaded, setScriptLoaded] = useState(false);
    const [mapOpen, setMapOpen] = useState(false);
    const [latitude, setLatitude] = useState(Number(event?.locationLatitude));
    const [longitude, setLongitude] = useState(Number(event?.locationLongitude));
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [errors, setErrors] = useState({});

    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markerInstance = useRef(null);
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    const computeErrors = () => {
        const e = {};

        // Zorunlu genel alanlar
        if (!eventTitle?.trim()) e.eventTitle = true;
        if (!eventDescription?.trim()) e.eventDescription = true;
        if (!startDate) e.startDate = true;
        if (!startTime) e.startTime = true;
        if (!endDate) e.endDate = true;
        if (!endTime) e.endTime = true;
        if (!organizedBy?.trim()) e.organizedBy = true;

        // Kapak görseli (edit'te de zorunlu)
        if (!selectedFiles?.length) e.cover = true;

        if (isOnline) {
            if (!isOnlineWebsite?.trim()) e.isOnlineWebsite = true;
        } else {
            if (!locationAddress?.trim()) e.locationAddress = true;
            if (!latitude || !longitude) e.latlng = true;
        }

        // Bilet alanları (açıksa zorunlu)
        if (hasTickets) {
            if (!ticketSaleStartDateTime?.date) e.ticketSaleDate = true;
            if (!ticketSaleStartDateTime?.time) e.ticketSaleTime = true;
            if (!ticketSaleNotificationEmail?.trim()) e.ticketNotifyEmail = true;
            if (!customerNotificationMailTemplate?.trim()) e.ticketCustomerTpl = true;
        }

        return e;
    };

    useEffect(() => {
        if (submitAttempted) setErrors(computeErrors());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        eventTitle, eventDescription, startDate, startTime, endDate, endTime,
        isOnline, isOnlineWebsite, locationAddress, latitude, longitude,
        organizedBy, hasTickets, ticketSaleStartDateTime,
        ticketSaleNotificationEmail, customerNotificationMailTemplate,
        selectedFiles // kapak değişince de tetiklensin
    ]);

    const hasError = (key) => submitAttempted && !!errors[key];


    const initMap = () => {
        if (!mapRef.current) return;

        // 1) Harita örneğini oluştur ve sakla
        mapInstance.current = new window.google.maps.Map(mapRef.current, {
            center: { lat: 0, lng: 0 },
            zoom: 2,
        });

        // 2) Marker örneğini oluştur (başlangıçta gizli pozisyonda)
        markerInstance.current = new window.google.maps.Marker({
            map: mapInstance.current,
            position: { lat: 0, lng: 0 },
            visible: false,
        });

        // 3) Haritaya tıklanınca hem state’i hem marker’ı güncelle
        mapInstance.current.addListener("click", (e) => {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();
            setLatitude(lat);
            setLongitude(lng);

            // Marker’ı göster ve pozisyonunu ayarla
            markerInstance.current.setPosition(e.latLng);
            markerInstance.current.setVisible(true);
        });

        // Eğer daha önce seçilmiş bir koordinat varsa, marker’ı yerleştir
        if (latitude && longitude) {
            const pos = { lat: parseFloat(latitude), lng: parseFloat(longitude) };
            markerInstance.current.setPosition(pos);
            markerInstance.current.setVisible(true);
            mapInstance.current.setCenter(pos);
            mapInstance.current.setZoom(12); // isteğe bağlı zoom ayarı
        }
    };

    useEffect(() => {
        if (event?.eventStartDateTime) {
            const start = new Date(Number(event.eventStartDateTime) * 1000);
            // Yerel tarih bileşenlerini al
            const year = start.getFullYear();
            const month = String(start.getMonth() + 1).padStart(2, "0");
            const day = String(start.getDate()).padStart(2, "0");
            setStartDate(`${year}-${month}-${day}`);

            const hours = String(start.getHours()).padStart(2, "0");
            const minutes = String(start.getMinutes()).padStart(2, "0");
            setStartTime(`${hours}:${minutes}`);
        }

        if (event?.eventEndDateTime) {
            const end = new Date(Number(event.eventEndDateTime) * 1000);
            const year = end.getFullYear();
            const month = String(end.getMonth() + 1).padStart(2, "0");
            const day = String(end.getDate()).padStart(2, "0");
            setEndDate(`${year}-${month}-${day}`);

            const hours = String(end.getHours()).padStart(2, "0");
            const minutes = String(end.getMinutes()).padStart(2, "0");
            setEndTime(`${hours}:${minutes}`);
        }
    }, [event?.eventID]);


    useEffect(() => {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            setScriptLoaded(true);
            if (mapOpen) initMap();
        };
        document.head.appendChild(script);
    }, []);

    useEffect(() => {
        if (scriptLoaded && mapOpen) {
            initMap();
        }
    }, [scriptLoaded, mapOpen]);

    if (!isOpen) return null;



    // EditEventModal içindeki resetStates fonksiyonunu şu şekilde güncelleyin:
    const resetStates = () => {
        setSelectedFiles([]);
        setEventTitle(event?.title || "");
        setEventDescription(event?.description || "");
        // Tarih ve saat için de useEffect’teki parsing mantığını kullanıyoruz:
        if (event?.eventStartDateTime) {
            const start = new Date(Number(event.eventStartDateTime) * 1000);
            const yyyy = start.getFullYear();
            const mm = String(start.getMonth() + 1).padStart(2, "0");
            const dd = String(start.getDate()).padStart(2, "0");
            setStartDate(`${yyyy}-${mm}-${dd}`);
            setStartTime(`${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`);
        } else {
            setStartDate("");
            setStartTime("");
        }

        if (event?.eventEndDateTime) {
            const end = new Date(Number(event.eventEndDateTime) * 1000);
            const yyyy = end.getFullYear();
            const mm = String(end.getMonth() + 1).padStart(2, "0");
            const dd = String(end.getDate()).padStart(2, "0");
            setEndDate(`${yyyy}-${mm}-${dd}`);
            setEndTime(`${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`);
        } else {
            setEndDate("");
            setEndTime("");
        }

        if (event?.ticketSaleStartDateTime) {
            const ts = new Date(Number(event.ticketSaleStartDateTime) * 1000);
            const yyyy = ts.getFullYear();
            const mm = String(ts.getMonth() + 1).padStart(2, "0");
            const dd = String(ts.getDate()).padStart(2, "0");
            const hh = String(ts.getHours()).padStart(2, "0");
            const mi = String(ts.getMinutes()).padStart(2, "0");
            setTicketSaleStartDateTime({ date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` });
        } else {
            setTicketSaleStartDateTime(null);
        }

        setSelectedFiles(getCoverFile());
        setIsOnlineWebsite(event?.website)
        setIsOnline(event?.isOnline === "1");
        setLocationAddress(event?.locationAddress || "");
        setOrganizedBy(event?.organizedBy || "");
        setHasTickets(event?.hasTickets === "1");
        setTicketSaleStartDateTime(event?.ticketSaleStartDateTime
            ? (() => {
                const ts = new Date(Number(event.ticketSaleStartDateTime) * 1000);
                const yyyy = ts.getFullYear();
                const mm = String(ts.getMonth() + 1).padStart(2, "0");
                const dd = String(ts.getDate()).padStart(2, "0");
                const hh = String(ts.getHours()).padStart(2, "0");
                const mi = String(ts.getMinutes()).padStart(2, "0");
                return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
            })()
            : null
        );
        setTicketSaleNotificationEmail(event?.ticketSaleNotificationEmail || "");
        setContactEmail(event?.contactEmail || "");
        setCustomerNotificationMailTemplate(event?.customerNotificationMailTemplate || "");
        setContactPhone(event?.contactPhone || "");
        setIsPublic(event?.isPublic);
        setWebsite(event?.socialMediaLinks?.website || "");
        setInstagram(event?.socialMediaLinks?.instagram || "");
        setX(event?.socialMediaLinks?.x || "");
        setUsersCanSendMessage(event?.usersCanSendMessage || false);

        // Harita değerleri
        setMapOpen(false);
        setLatitude(Number(event?.locationLatitude) || "");
        setLongitude(Number(event?.locationLongitude) || "");
    };

    // AddEventModal fonksiyonunuzun en üstüne (useState’lerin altına) ekleyin:
    const allFieldsAreValid = () => {
        // 1. Ana etkinlik alanları
        if (
            !eventTitle ||
            !eventDescription ||
            !startDate ||
            !startTime ||
            !endDate ||
            !endTime ||
            !organizedBy ||
            (isOnline
                ? !isOnlineWebsite
                : !locationAddress)
        ) {
            return false;
        }

        if (hasTickets) {
            if (!ticketSaleStartDateTime?.date || !ticketSaleStartDateTime?.time || !ticketSaleNotificationEmail || !customerNotificationMailTemplate) {
                return false;
            }
        }

        if (!isOnline && (!latitude || !longitude)) {
            return false;
        }

        return true;
    };

    const formatDateTime = (date, time) => {
        const d = typeof date === 'string' ? new Date(date) : new Date(date.toString());
        let hours, minutes;

        // AM/PM varsa ayır
        const hasAMPM = /am|pm/i.test(time);
        if (hasAMPM) {
            const timeMatch = time.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);
            if (timeMatch) {
                hours = parseInt(timeMatch[1], 10);
                minutes = parseInt(timeMatch[2], 10);
                const meridiem = timeMatch[3].toLowerCase();

                if (meridiem === 'pm' && hours !== 12) hours += 12;
                if (meridiem === 'am' && hours === 12) hours = 0;
            } else {
                console.warn("Time format is not valid:", time);
                return '';
            }
        } else {
            [hours, minutes] = time.split(':').map(Number);
        }

        d.setHours(hours, minutes);

        const pad = (num) => String(num).padStart(2, '0');
        const YYYY = d.getFullYear();
        const MM = pad(d.getMonth() + 1);
        const DD = pad(d.getDate());
        const hh = pad(d.getHours());
        const mm = pad(d.getMinutes());

        return `${YYYY}-${MM}-${DD} ${hh}:${mm}`;
    };


    const updateEvent = () => {
        setSubmitAttempted(true);
        const e = computeErrors();
        setErrors(e);
        if (Object.keys(e).length > 0) {
            toastWarning(getTranslation("pleaseFillAllFields"));
            return;
        }

        setCreateProjectLoading(true)

        const cover =
            selectedFiles[0] && selectedFiles[0].file
                ? {
                    file: selectedFiles[0].file,
                    extension: selectedFiles[0].extension,
                }
                : undefined;

        // 2. Event başlama / bitiş zamanları
        const eventStartDateTime = formatDateTime(startDate, startTime);
        const eventEndDateTime = formatDateTime(endDate, endTime);

        // 3. Ticket satış zamanı
        const ticketSaleStart = hasTickets
            ? formatDateTime(ticketSaleStartDateTime.date, ticketSaleStartDateTime.time)
            : null;


        // 4. Sosyal medya linkleri
        const socialMediaLinks = {
            website,
            instagram,
            x,
        };

        const payload = {
            eventID: event.eventID,
            channelID: event.channelID,
            title: eventTitle,
            description: eventDescription,
            eventStartDateTime,
            eventEndDateTime,
            isOnline,
            website: isOnline ? isOnlineWebsite : "",
            locationAddress: !isOnline ? locationAddress : "",
            locationLatitude: !isOnline ? latitude : "",
            locationLongitude: !isOnline ? longitude : "",
            organizedBy,
            hasTickets,
            ticketSaleStartDateTime: hasTickets ? ticketSaleStart : null,
            contactEmail,
            ticketSaleNotificationEmail: hasTickets ? ticketSaleNotificationEmail : "",
            customerNotificationMailTemplate,
            contactPhone,
            socialMediaLinks,
            usersCanSendMessage,
            isPublic,
            status: 1,
        };

        if (cover) {
            payload.cover = cover;
        }

        Services.SocialHiveUpdateEvent(token, payload)
            .then((res) => {
                setCreateProjectLoading(false)
                if (res.data.status) {
                    toastSuccess(getTranslation("successMessage"));
                    onClose();
                    resetStates();
                    getEvents();
                } else {
                    toastWarning(res.data.message || getTranslation("somethingWentWrong"));
                }
            })
            .catch((err) => {
                console.error(err);
                toastWarning(getTranslation("somethingWentWrong"));
            });
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        files.forEach((file) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result.split(",")[1]; // "data:image/jpeg;base64," kısmını kaldırıyoruz
                const extension = file.name.split(".").pop();
                const title =
                    file?.name?.substring(0, file.name.lastIndexOf(".")) || file?.name;
                setSelectedFiles((prevFiles) => [
                    ...prevFiles,
                    { file: base64String, extension: extension, title: title },
                ]);
            };
            reader.readAsDataURL(file);
        });
    };

    const removeFile = (index) => {
        setSelectedFiles((prevFiles) => prevFiles.filter((file, i) => i !== index));
    };

    return (
        <div className="add-event-modal-overlay">
            <div className="add-event-modal-container">
                <IoCloseOutline
                    className="add-event-modal-button close"
                    onClick={() => { onClose(); resetStates(); }}
                />

                <p className="add-event-modal-title">{getTranslation("updateEvent")}</p>

                <div style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: '15px' }}>

                    <span
                        className="add-channel-modal-title"
                        onClick={() => { setIsOnline(!isOnline); setMapOpen(false); setLatitude(""); setLongitude(""); }}
                        style={{
                            alignItems: "center",
                            gap: "15px",
                            display: "flex",
                            flexDirection: "row",
                            fontWeight: "500",
                            fontSize: "14px",
                        }}
                    >
                        <input type="checkbox" checked={isOnline} onChange={(e) => { setIsOnline(e.target.checked); setMapOpen(false); setLatitude(""); setLongitude(""); }} />
                        {getTranslation("isOnline")}
                    </span>

                    <span
                        className="add-channel-modal-title"
                        onClick={() => setIsPublic(!isPublic)}
                        style={{
                            alignItems: "center",
                            gap: "15px",
                            display: "flex",
                            flexDirection: "row",
                            fontWeight: "500",
                            fontSize: "14px",
                        }}
                    >
                        <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
                        {getTranslation("isPublic")}
                    </span>

                    {/* <span
                        className="add-channel-modal-title"
                        onClick={() => setUsersCanSendMessage(!usersCanSendMessage)}
                        style={{
                            alignItems: "center",
                            gap: "15px",
                            display: "flex",
                            flexDirection: "row",
                            fontWeight: "500",
                            fontSize: "14px",
                        }}
                    >
                        <input type="checkbox" checked={usersCanSendMessage} onChange={(e) => setUsersCanSendMessage(e.target.checked)} />
                        {getTranslation("usersCanSendMessage")}
                    </span> */}

                </div>


                <div className={hasError("eventTitle") ? "invalid-field" : ""}>
                    <AddModalInputField
                        icon={<TbStack2 size={22} style={{ width: "25px" }} />}
                        placeholder={getTranslation("eventName") + "*"}
                        value={eventTitle}
                        onChange={(e) => setEventTitle(e.target.value)}
                        type="text"
                    />
                </div>

                <div className={`add-task-modal-content ${hasError("eventDescription") ? "invalid-textarea" : ""}`}>
                    <IoDocumentTextOutline
                        size={20}
                        style={{
                            width: "25px",
                            alignSelf: "flex-start",
                            marginTop: "10px",
                        }}
                    />
                    <textarea
                        className={`add-task-modal-textarea`}
                        placeholder={getTranslation("description") + "*"}
                        value={eventDescription}
                        rows={6}
                        onChange={(e) => setEventDescription(e.target.value)}
                    ></textarea>
                </div>

                <div className="add-task-modal-date-time-picker-container">
                    <div className={hasError("startDate") ? "invalid-field" : ""} style={{ width: "100%" }}>
                        <DatePicker
                            style={{ width: "100%" }}
                            buttonStyle={{ width: "100%" }}
                            initialDate={startDate}
                            onDateSelect={setStartDate}
                            toggleLabel={getTranslation("startDate") + "*"}
                        />
                    </div>
                    <div className={hasError("startTime") ? "invalid-field" : ""} style={{ width: "100%" }}>
                        <TimePicker
                            style={{ width: "100%" }}
                            buttonStyle={{ width: "100%" }}
                            initialTime={startTime}
                            onTimeSelect={setStartTime}
                            toggleLabel={getTranslation("startTime") + "*"}
                            quarterIntervals
                        />
                    </div>
                </div>

                <div className="add-task-modal-date-time-picker-container">
                    <div className={hasError("endDate") ? "invalid-field" : ""} style={{ width: "100%" }}>
                        <DatePicker
                            style={{ width: "100%" }}
                            buttonStyle={{ width: "100%" }}
                            onDateSelect={setEndDate}
                            initialDate={endDate}
                            toggleLabel={getTranslation("endDate") + "*"}
                        />
                    </div>
                    <div className={hasError("endTime") ? "invalid-field" : ""} style={{ width: "100%" }}>
                        <TimePicker
                            style={{ width: "100%" }}
                            buttonStyle={{ width: "100%" }}
                            onTimeSelect={setEndTime}
                            initialTime={endTime}
                            toggleLabel={getTranslation("endTime") + "*"}
                            quarterIntervals
                        />
                    </div>
                </div>

                {isOnline && (
                    <div className={hasError("isOnlineWebsite") ? "invalid-field" : ""}>
                        <AddModalInputField
                            placeholder={getTranslation("website") + "*"}
                            value={isOnlineWebsite}
                            onChange={(e) => setIsOnlineWebsite(e.target.value)}
                        />
                    </div>
                )}

                {!isOnline && (
                    <>
                        <div className={hasError("locationAddress") ? "invalid-field" : ""}>
                            <AddModalInputField
                                placeholder={getTranslation("locationAddress") + "*"}
                                value={locationAddress}
                                onChange={(e) => setLocationAddress(e.target.value)}
                            />
                        </div>
                        {(latitude && longitude) ? (
                            <span style={{ color: 'var(--add-modal-placeholder-color)', fontWeight: 400 }}>
                                {getTranslation("lat")}: {latitude.toFixed(6)}, {getTranslation("lng")}: {longitude.toFixed(6)}
                            </span>
                        ) : (

                            <span style={{ color: 'var(--add-modal-placeholder-color)', fontWeight: 400 }}>
                                {getTranslation("lat")}*: -, {getTranslation("lng")}*: -
                            </span>
                        )
                        }

                        <div>
                            <button
                                type="button"
                                className={`action-button  ${hasError("latlng") ? "invalid-textarea" : ""}`}
                                style={{ marginTop: '8px' }}
                                onClick={() => setMapOpen(!mapOpen)}
                            >
                                {mapOpen ? getTranslation("hideMap") : getTranslation("showMap")}
                            </button>
                        </div>
                        {mapOpen && (
                            <div className="map-picker-container" style={{ marginTop: '10px' }}>
                                <div
                                    id="map"
                                    ref={mapRef}
                                    style={{ height: '300px', width: '100%' }}
                                />
                            </div>
                        )}
                    </>
                )}

                <div className={hasError("organizedBy") ? "invalid-field" : ""}>
                    <AddModalInputField
                        placeholder={getTranslation("organizedBy") + "*"}
                        value={organizedBy}
                        onChange={(e) => setOrganizedBy(e.target.value)}
                    />
                </div>

                <div
                    className={hasError("cover") ? "invalid-field" : ""}
                    style={{
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "6px",
                        display: "flex",
                    }}
                >
                    <span
                        style={{
                            width: "100%",
                            flexDirection: "row",
                            alignItems: "center",
                            display: "flex",
                            gap: "5px",
                        }}
                    >
                        <span className="add-event-modal-attachments-icon">
                            <GrAttachment />
                        </span>
                        <span className="add-event-modal-attachments">
                            {getTranslation("uploadBannerImage")}
                        </span>
                    </span>
                    <div className="add-event-modal-select-file-holder">
                        <button
                            className="action-button"
                            onClick={() => fileInputRef.current.click()}
                        >
                            {getTranslation("selectFile")}
                        </button>
                        <input
                            type="file"
                            accept=".png,.jpg,.jpeg,.gif"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                        />

                    </div>


                    {/* Seçilen dosyaların chip'lerini gösteren alan */}
                    {selectedFiles.length > 0 && (
                        <div
                            style={{
                                display: "flex",
                                overflowX: "auto",
                                gap: "8px",
                                padding: "8px 0",
                                width: "100%",
                            }}
                        >
                            {selectedFiles.map((file, index) => (
                                <div
                                    className="add-event-modal-added-attachment-chip"
                                    key={index}
                                >
                                    <span className="add-event-modal-added-attachment-chip-title">
                                        {file.title}
                                    </span>
                                    <CgCloseO
                                        onClick={() => removeFile(index)}
                                        style={{
                                            cursor: "var(--pointer)",
                                            minWidth: "14px",
                                            minHeight: "14px",
                                            width: "14px",
                                            height: "14px",
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {selectedFiles.length > 0 && selectedFiles[0].url && (
                        <div className="add-event-modal-cover-preview">
                            <img
                                src={selectedFiles[0].url}
                                alt="Mevcut kapak"
                                style={{ width: "100%", height: "auto", marginBottom: "10px", borderRadius: "4px" }}
                            />
                        </div>
                    )}

                </div>

                <AddModalInputField placeholder={getTranslation("contactEmail")} value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                <AddModalInputField placeholder={getTranslation("contactPhone")} value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
                <AddModalInputField icon={<TbWorld size={22} style={{ width: "25px" }} />} placeholder={getTranslation("website")} value={website} onChange={(e) => setWebsite(e.target.value)} />
                <AddModalInputField icon={<TbBrandInstagram size={22} style={{ width: "25px" }} />} placeholder={getTranslation("Instagram")} value={instagram} onChange={(e) => setInstagram(e.target.value)} />
                <AddModalInputField icon={<RiTwitterXFill size={20} style={{ width: "25px" }} />} placeholder={getTranslation("X")} value={x} onChange={(e) => setX(e.target.value)} />


                <span
                    className="add-channel-modal-title"
                    style={{
                        alignItems: "center",
                        gap: "15px",
                        display: "flex",
                        flexDirection: "row",
                        fontWeight: "500",
                        fontSize: "14px",
                    }}
                >
                    <input type="checkbox" checked={hasTickets} onChange={(e) => setHasTickets(e.target.checked)} />
                    {getTranslation("hasTickets")}
                </span>

                {hasTickets &&
                    <>
                        <div className="add-task-modal-date-time-picker-container">
                            <div className={hasError("ticketSaleDate") ? "invalid-field" : ""} style={{ width: "100%" }}>
                                <DatePicker
                                    style={{ width: "100%" }}
                                    buttonStyle={{ width: "100%" }}
                                    initialDate={ticketSaleStartDateTime?.date || ""}
                                    onDateSelect={(date) => setTicketSaleStartDateTime(prev => ({ ...prev, date }))}
                                    toggleLabel={getTranslation("ticketSaleStartDate") + "*"}
                                />
                            </div>
                            <div className={hasError("ticketSaleTime") ? "invalid-field" : ""} style={{ width: "100%" }}>
                                <TimePicker
                                    style={{ width: "100%" }}
                                    buttonStyle={{ width: "100%" }}
                                    initialTime={ticketSaleStartDateTime?.time || ""}
                                    onTimeSelect={(time) => setTicketSaleStartDateTime(prev => ({ ...prev, time }))}
                                    toggleLabel={getTranslation("ticketSaleStartTime") + "*"}
                                    quarterIntervals
                                />
                            </div>
                        </div>

                        <div className={hasError("ticketNotifyEmail") ? "invalid-field" : ""} style={{ marginTop: 15 }}>
                            <AddModalInputField
                                placeholder={getTranslation("ticketSaleNotificationEmail") + "*"}
                                value={ticketSaleNotificationEmail}
                                onChange={(e) => setTicketSaleNotificationEmail(e.target.value)}
                            />
                        </div>

                    </>

                }

                {hasTickets &&
                    <div className={`add-task-modal-content ${hasError("ticketCustomerTpl") ? "invalid-textarea" : ""}`}>
                        <textarea className={`add-task-modal-textarea`}
                            style={{ height: '250px' }} placeholder={getTranslation("customerNotificationMailTemplate") + "*"} value={customerNotificationMailTemplate} onChange={(e) => setCustomerNotificationMailTemplate(e.target.value)} />
                    </div>
                }

                <button
                    className="add-event-modal-button create"
                    onClick={updateEvent}
                >
                    {getTranslation("update")}
                </button>

            </div>
        </div>
    );
}
